import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, ArrowLeft } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import FloatingHearts from "../components/FloatingHearts";
import BottomTabBar from "../components/BottomTabBar";

export default function Login() {
  const nav = useNavigate();
  const { login, register } = useAuth();
  const [tab, setTab] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (tab === "login") {
        await login(email, password);
        toast.success("Welcome back!");
      } else {
        if (!name.trim()) return toast.error("Name required");
        await register(name.trim(), email, password);
        toast.success("Account created!");
      }
      nav("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="warm-bg min-h-screen flex items-center justify-center p-6 relative overflow-hidden">
      <FloatingHearts count={6} />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 w-full max-w-md"
      >
        <Link to="/" className="inline-flex items-center gap-2 text-[#C9C7D9] hover:text-white font-semibold mb-6" data-testid="login-back-link">
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>

        <div className="card-glow rounded-3xl p-9">
          <div className="flex items-center gap-3 mb-7">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-[#FF4D8D] to-[#7B61FF] flex items-center justify-center pulse-glow">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <h1 className="font-display text-2xl font-bold text-white">
              {tab === "login" ? "Welcome back" : "Create account"}
            </h1>
          </div>

          <div className="flex bg-white/5 rounded-full p-1 mb-7 border border-white/5">
            {["login", "register"].map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                data-testid={`tab-${t}`}
                className={`flex-1 py-2.5 rounded-full font-semibold text-sm transition ${
                  tab === t ? "btn-gradient text-white" : "text-[#C9C7D9]"
                }`}
              >
                {t === "login" ? "Sign in" : "Register"}
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="space-y-4">
            {tab === "register" && (
              <Field label="Name" type="text" value={name} onChange={setName} testid="register-name-input" />
            )}
            <Field label="Email" type="email" value={email} onChange={setEmail} required testid="email-input" />
            <Field label="Password" type="password" value={password} onChange={setPassword} required minLength={6} testid="password-input" />
            <button
              type="submit"
              disabled={loading}
              className="w-full btn-gradient rounded-full py-3.5 font-bold text-lg disabled:opacity-60 active:scale-95 transition"
              data-testid="submit-auth-button"
            >
              {loading ? "..." : tab === "login" ? "Sign in" : "Create account"}
            </button>
          </form>
          <p className="text-xs text-center text-[#C9C7D9] mt-6">
            Login is optional. You can play without an account.
          </p>
        </div>
      </motion.div>
      <BottomTabBar active="profile" />
    </div>
  );
}

function Field({ label, type, value, onChange, required, minLength, testid }) {
  return (
    <div>
      <label className="block text-xs font-bold text-[#C9C7D9] mb-2 ml-1 uppercase tracking-[0.2em]">{label}</label>
      <input
        type={type}
        required={required}
        minLength={minLength}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-3.5 text-white placeholder:text-[#C9C7D9]/50 focus:outline-none focus:ring-2 focus:ring-[#FF4D8D]/60"
        data-testid={testid}
      />
    </div>
  );
}
